import React, { useState, useEffect, useCallback } from 'react';
import { creatorAPI } from '../../services/api';
import { Copy, CheckCircle, Loader, AlertCircle, ArrowRight, Shield, XCircle, FileText } from 'lucide-react';

const ChannelClaimWizard = ({ currentUser, claim: initialClaim, onClaimUpdate }) => {
  const [step, setStep] = useState(1);
  const [channelInput, setChannelInput] = useState('');
  const [validatedChannel, setValidatedChannel] = useState(null); // { channelId, channelName, channelAvatar }
  const [consentId, setConsentId] = useState(null);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [consentText, setConsentText] = useState(null);
  const [showExclusionConfirm, setShowExclusionConfirm] = useState(false);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [method, setMethod] = useState('channel_description');
  const [videoId, setVideoId] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);

  // Sync with parent's claim on mount/update
  useEffect(() => {
    if (initialClaim) {
      setClaim(initialClaim);
      if (initialClaim.status === 'verified' || initialClaim.status === 'admin_approved') {
        setStep(5);
      } else if (initialClaim.status === 'code_issued') {
        setStep(3);
      } else if (initialClaim.status === 'failed') {
        setStep(4);
      }
    }
  }, [initialClaim]);

  // Fetch consent text on mount
  useEffect(() => {
    creatorAPI.getConsentText()
      .then(data => { if (data.success) setConsentText(data); })
      .catch(() => {});
  }, []);

  // Step 1: Validate channel (no DB writes, just preview)
  const handleValidateChannel = useCallback(async () => {
    if (!channelInput.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await creatorAPI.validateChannel(channelInput.trim());
      if (data.success) {
        setValidatedChannel({
          channelId: data.channelId,
          channelName: data.channelName,
          channelAvatar: data.channelAvatar
        });
        setStep(2);
      } else {
        setError(data.error || 'Unexpected response');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to validate channel');
    } finally {
      setLoading(false);
    }
  }, [channelInput]);

  // Step 2: Submit consent then initiate claim
  const handleConsentAndInitiate = async () => {
    if (!consentAgreed) return;
    setLoading(true);
    setError('');
    try {
      // 1. Submit consent
      const consentData = await creatorAPI.submitConsent({
        consentType: 'creator_data_usage',
        agreed: true,
        channelId: validatedChannel.channelId,
        channelName: validatedChannel.channelName
      });
      if (!consentData.success) {
        setError(consentData.error || 'Failed to record consent');
        return;
      }
      const newConsentId = consentData.consentId;
      setConsentId(newConsentId);

      // 2. Initiate claim with consent reference
      const claimData = await creatorAPI.initiateClaim(channelInput.trim(), newConsentId);
      if (claimData.success && claimData.claim) {
        setClaim(claimData.claim);
        setStep(3);
      } else {
        setError(claimData.error || 'Unexpected response');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate claim');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 alt: Request data exclusion
  const handleRequestExclusion = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await creatorAPI.requestExclusion({
        channelId: validatedChannel.channelId,
        channelName: validatedChannel.channelName
      });
      if (data.success) {
        setShowExclusionConfirm(true);
      } else {
        setError(data.error || 'Failed to submit exclusion request');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit exclusion request');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify
  const handleVerify = async () => {
    setLoading(true);
    setError('');
    setVerifyResult(null);
    try {
      const data = await creatorAPI.verifyClaim(method, videoId || undefined);
      if (data.status === 'verified') {
        setStep(5);
        if (onClaimUpdate) setTimeout(onClaimUpdate, 2000);
      } else {
        setVerifyResult(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    const code = claim?.verificationCode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTimeRemaining = () => {
    if (!claim?.codeExpiresAt) return '';
    const diff = new Date(claim.codeExpiresAt) - new Date();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m remaining`;
  };

  const styles = {
    wizard: {
      maxWidth: '620px',
      background: '#1A1A1A',
      borderRadius: '12px',
      border: '1px solid #2A2A2A',
      padding: '32px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: '8px',
    },
    subtitle: {
      color: '#888',
      fontSize: '14px',
      marginBottom: '24px',
      lineHeight: '1.5',
    },
    stepIndicator: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
    },
    stepDot: (active, completed) => ({
      flex: 1,
      height: '4px',
      borderRadius: '2px',
      background: completed ? '#8B5CF6' : active ? '#8B5CF6' : '#333',
      transition: 'all 0.3s',
    }),
    input: {
      width: '100%',
      padding: '12px 16px',
      background: '#0F0F0F',
      border: '1px solid #333',
      borderRadius: '8px',
      color: '#E5E5E5',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    button: (disabled) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      padding: '12px',
      border: 'none',
      borderRadius: '8px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      background: disabled ? '#333' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
      color: '#FFFFFF',
      marginTop: '16px',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.2s',
    }),
    codeContainer: {
      background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(109,40,217,0.1))',
      border: '2px solid #8B5CF6',
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center',
      marginBottom: '20px',
    },
    codeLabel: {
      fontSize: '11px',
      fontWeight: '700',
      color: '#8B5CF6',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      marginBottom: '12px',
    },
    codeValue: {
      fontFamily: "'Courier New', Courier, monospace",
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#FFFFFF',
      letterSpacing: '3px',
      padding: '12px',
      background: 'rgba(0,0,0,0.4)',
      borderRadius: '8px',
      marginBottom: '12px',
      wordBreak: 'break-all',
      userSelect: 'all',
    },
    copyBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 20px',
      background: '#8B5CF6',
      border: 'none',
      borderRadius: '6px',
      color: '#FFFFFF',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      transition: 'all 0.2s',
    },
    expiryBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 12px',
      background: 'rgba(245,158,11,0.15)',
      border: '1px solid rgba(245,158,11,0.3)',
      borderRadius: '16px',
      fontSize: '12px',
      color: '#F59E0B',
      fontWeight: '600',
      marginTop: '8px',
    },
    instructionBox: {
      background: '#0F0F0F',
      border: '1px solid #2A2A2A',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    instructionStep: {
      display: 'flex',
      gap: '10px',
      marginBottom: '10px',
      fontSize: '13px',
      color: '#CCC',
      lineHeight: '1.4',
    },
    instructionNum: {
      width: '22px',
      height: '22px',
      borderRadius: '50%',
      background: '#8B5CF6',
      color: '#FFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 'bold',
      flexShrink: 0,
    },
    methodRadio: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      margin: '16px 0',
    },
    radioLabel: (selected) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 14px',
      background: selected ? 'rgba(139, 92, 246, 0.1)' : '#0F0F0F',
      border: `1px solid ${selected ? '#8B5CF6' : '#333'}`,
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '13px',
      color: selected ? '#E5E5E5' : '#888',
    }),
    error: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#EF4444',
      fontSize: '13px',
      marginTop: '12px',
    },
    successContainer: {
      textAlign: 'center',
      padding: '40px 0',
    },
    pendingContainer: {
      textAlign: 'center',
      padding: '40px 0',
      color: '#888',
    },
    // Consent step styles
    channelPreview: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      background: '#0F0F0F',
      border: '1px solid #2A2A2A',
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '20px',
    },
    channelAvatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      objectFit: 'cover',
      background: '#333',
    },
    consentBox: {
      maxHeight: '240px',
      overflowY: 'auto',
      background: '#0F0F0F',
      border: '1px solid #333',
      borderRadius: '8px',
      padding: '16px',
      fontSize: '12px',
      lineHeight: '1.7',
      color: '#BBB',
      whiteSpace: 'pre-wrap',
      marginBottom: '16px',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      marginBottom: '8px',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      accentColor: '#8B5CF6',
      cursor: 'pointer',
      marginTop: '2px',
      flexShrink: 0,
    },
    exclusionLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: 'none',
      border: 'none',
      color: '#EF4444',
      fontSize: '13px',
      cursor: 'pointer',
      padding: '8px 0',
      textDecoration: 'underline',
    },
    smallPrint: {
      fontSize: '11px',
      color: '#555',
      marginTop: '8px',
    },
    exclusionConfirm: {
      textAlign: 'center',
      padding: '40px 0',
    },
  };

  // If exclusion was submitted, show confirmation instead of normal flow
  if (showExclusionConfirm) {
    return (
      <div style={styles.wizard}>
        <div style={styles.exclusionConfirm}>
          <XCircle size={56} style={{ color: '#EF4444', marginBottom: '16px' }} />
          <div style={styles.title}>Exclusion Request Submitted</div>
          <div style={{ color: '#888', marginTop: '8px', fontSize: '14px', lineHeight: '1.6' }}>
            Your request to exclude <strong style={{ color: '#FFF' }}>{validatedChannel?.channelName}</strong>'s
            data from ReactorHub has been submitted.<br /><br />
            An admin will review your request within <strong style={{ color: '#FFF' }}>30 business days</strong>.
            You will receive a notification when it's processed.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wizard}>
      <div style={styles.stepIndicator}>
        {[1, 2, 3, 4, 5].map(s => (
          <div key={s} style={styles.stepDot(s === step, s < step)} />
        ))}
      </div>

      {/* STEP 1: Enter Channel ID/URL */}
      {step === 1 && (
        <>
          <div style={styles.title}>Claim Your Creator Profile</div>
          <div style={styles.subtitle}>
            Enter your YouTube channel ID to start the verification process and unlock Creator Studio.
          </div>
          <input
            style={styles.input}
            placeholder="UCxxxxxx... or https://youtube.com/@handle"
            value={channelInput}
            onChange={e => setChannelInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleValidateChannel()}
          />
          <div style={{ fontSize: '12px', color: '#555', marginTop: '6px' }}>
            Accepts: Channel ID (UC...), youtube.com/@handle, youtube.com/channel/UC...
          </div>
          {error && <div style={styles.error}><AlertCircle size={14} /> {error}</div>}
          <button
            style={styles.button(loading || !channelInput.trim())}
            onClick={handleValidateChannel}
            disabled={loading || !channelInput.trim()}
          >
            {loading
              ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Validating...</>
              : <>Continue <ArrowRight size={16} /></>
            }
          </button>
        </>
      )}

      {/* STEP 2: Data Usage Consent */}
      {step === 2 && (
        <>
          <div style={styles.title}>
            <FileText size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px', color: '#8B5CF6' }} />
            Data Usage Agreement
          </div>
          <div style={styles.subtitle}>
            Please review and agree to the data usage terms before proceeding with verification.
          </div>

          {/* Channel preview */}
          <div style={styles.channelPreview}>
            {validatedChannel?.channelAvatar ? (
              <img src={validatedChannel.channelAvatar} alt="" style={styles.channelAvatar} />
            ) : (
              <div style={{ ...styles.channelAvatar, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#888' }}>?</div>
            )}
            <div>
              <div style={{ color: '#FFF', fontWeight: '600', fontSize: '15px' }}>{validatedChannel?.channelName || 'Unknown Channel'}</div>
              <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>{validatedChannel?.channelId}</div>
            </div>
          </div>

          {/* Agreement text */}
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFF', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Shield size={14} style={{ color: '#8B5CF6' }} />
            {consentText?.title || 'Creator Data Usage Agreement'}
            {consentText?.version && <span style={{ fontSize: '11px', color: '#666', fontWeight: '400' }}>v{consentText.version}</span>}
          </div>
          <div style={styles.consentBox}>
            {consentText?.text || 'Loading agreement text...'}
          </div>

          {/* Agree checkbox */}
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={consentAgreed}
              onChange={e => setConsentAgreed(e.target.checked)}
              style={styles.checkbox}
            />
            <span style={{ fontSize: '13px', color: '#CCC', lineHeight: '1.4' }}>
              I have read and agree to the Creator Data Usage Agreement
            </span>
          </label>

          <div style={styles.smallPrint}>
            This consent is recorded and timestamped as proof of agreement.
          </div>

          {error && <div style={styles.error}><AlertCircle size={14} /> {error}</div>}

          <button
            style={styles.button(loading || !consentAgreed)}
            onClick={handleConsentAndInitiate}
            disabled={loading || !consentAgreed}
          >
            {loading
              ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</>
              : <>Continue to Verification <ArrowRight size={16} /></>
            }
          </button>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button style={styles.exclusionLink} onClick={handleRequestExclusion} disabled={loading}>
              <XCircle size={14} /> I want my data excluded from ReactorHub
            </button>
          </div>
        </>
      )}

      {/* STEP 3: Verification Code */}
      {step === 3 && (
        <>
          <div style={styles.title}>
            <Shield size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px', color: '#8B5CF6' }} />
            Verify Channel Ownership
          </div>
          <div style={styles.subtitle}>
            We generated a unique verification code for you. Place it on your YouTube channel, then come back and click "Verify Now".
          </div>

          <div style={styles.codeContainer}>
            <div style={styles.codeLabel}>Your Verification Code</div>
            <div style={styles.codeValue}>
              {claim?.verificationCode || '\u2014'}
            </div>
            <button style={styles.copyBtn} onClick={copyCode}>
              {copied
                ? <><CheckCircle size={14} /> Copied to Clipboard!</>
                : <><Copy size={14} /> Copy Code</>
              }
            </button>
            <div>
              <span style={styles.expiryBadge}>
                <AlertCircle size={12} /> {getTimeRemaining()}
              </span>
            </div>
          </div>

          <div style={styles.instructionBox}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFF', marginBottom: '12px' }}>How to verify:</div>
            <div style={styles.instructionStep}>
              <span style={styles.instructionNum}>1</span>
              <span>Copy the code above</span>
            </div>
            <div style={styles.instructionStep}>
              <span style={styles.instructionNum}>2</span>
              <span>Go to your YouTube channel and paste it in your channel description (About section), or in a video title/description</span>
            </div>
            <div style={styles.instructionStep}>
              <span style={styles.instructionNum}>3</span>
              <span>Select where you placed it below, then click <strong>"Verify Now"</strong></span>
            </div>
            <div style={styles.instructionStep}>
              <span style={styles.instructionNum}>4</span>
              <span>After verification, you can remove the code from your channel</span>
            </div>
          </div>

          <div style={{ color: '#888', fontSize: '13px', marginBottom: '8px' }}>Where did you place the code?</div>
          <div style={styles.methodRadio}>
            {[
              { id: 'channel_description', label: 'Channel About / Description' },
              { id: 'video_title', label: 'Video Title (any recent video)' },
              { id: 'video_description', label: 'Video Description (any recent video)' },
            ].map(opt => (
              <label key={opt.id} style={styles.radioLabel(method === opt.id)}>
                <input
                  type="radio"
                  name="method"
                  value={opt.id}
                  checked={method === opt.id}
                  onChange={() => setMethod(opt.id)}
                  style={{ accentColor: '#8B5CF6' }}
                />
                {opt.label}
              </label>
            ))}
          </div>

          {(method === 'video_title' || method === 'video_description') && (
            <input
              style={{ ...styles.input, marginTop: '8px' }}
              placeholder="YouTube Video ID (e.g., dQw4w9WgXcQ)"
              value={videoId}
              onChange={e => setVideoId(e.target.value)}
            />
          )}

          {verifyResult && !verifyResult.success && (
            <div style={{ ...styles.error, color: '#F59E0B' }}>
              <AlertCircle size={14} />
              {verifyResult.message}
              {verifyResult.attemptsUsed && ` (Attempt ${verifyResult.attemptsUsed})`}
            </div>
          )}
          {error && <div style={styles.error}><AlertCircle size={14} /> {error}</div>}

          <button
            style={styles.button(loading)}
            onClick={handleVerify}
            disabled={loading}
          >
            {loading
              ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Checking YouTube...</>
              : <>Verify Now <CheckCircle size={16} /></>
            }
          </button>
        </>
      )}

      {/* STEP 4: Pending Admin Review */}
      {step === 4 && (
        <div style={styles.pendingContainer}>
          <Loader size={40} style={{ color: '#8B5CF6', marginBottom: '16px', animation: 'spin 2s linear infinite' }} />
          <div style={styles.title}>Pending Admin Review</div>
          <div style={{ color: '#888', marginTop: '8px', fontSize: '14px' }}>
            Your claim is being reviewed by our team. You'll receive a notification once it's processed.
          </div>
        </div>
      )}

      {/* STEP 5: Success */}
      {step === 5 && (
        <div style={styles.successContainer}>
          <CheckCircle size={56} style={{ color: '#8B5CF6', marginBottom: '16px' }} />
          <div style={styles.title}>Profile Verified!</div>
          <div style={{ color: '#888', marginTop: '8px', fontSize: '14px' }}>
            Your creator profile has been verified. Welcome to Creator Studio!
          </div>
          <button
            style={styles.button(false)}
            onClick={() => onClaimUpdate && onClaimUpdate()}
          >
            Go to Dashboard <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChannelClaimWizard;
